<template>
  <div
    class="flex flex-col justify-center items-center min-h-fill"
    v-if="directory"
  >
    <div
      class="bg-[url('https://ik.imagekit.io/xposuremusic/industry-pros-header-new_dz7q2vN4d.png?updatedAt=1699347578632')] w-full bg-no-repeat text-center h-[530px] absolute top-0"
      style="background-position-x: center"
    />
    <div
      class="flex flex-col pb-28 px-8 lg:px-0 gap-6 w-auto sm:w-5/6 md:max-w-xl lg:max-w-4xl xl:max-w-1120px flex-grow z-10 mt-10"
    >
      <div class="flex gap-1 items-center">
        <router-link class="text-accent-orange" to="/industry-pros">
          All Pros
        </router-link>
        <template
          v-for="pathParam in directory.breadcrumbs?.slice(0, -1)"
          :key="pathParam"
        >
          <i class="icon-right" />
          <router-link class="text-accent-orange" :to="`${pathParam.link}`">
            {{ pathParam.title }}
          </router-link>
        </template>
        <i class="icon-right" />
        <p class="text-accent-orange">
          {{ directory.breadcrumbs?.slice(-1)[0].title }}
        </p>
      </div>

      <div
        class="flex flex-col lg:flex-row gap-0 lg:justify-between h-auto lg:items-center relative"
      >
        <h1
          class="text-xl text-grayscale-96 leading-160 font-druk-wide uppercase lg:mr-16"
        >
          {{ directory.title }}
        </h1>
        <button class="self-end" @click="showOrdersChanged()">
          <i
            :class="[
              query.order && query.column
                ? 'text-accent-orange'
                : 'text-grayscale-96',
              'icon-arrow-sort text-20 leading-5',
            ]"
          />
        </button>
      </div>

      <div class="min-h-60 flex flex-col gap-4">
        <div
          name="list"
          tag="container"
          class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-x-6 gap-y-12"
        >
          <Pro v-for="(pro, index) in pros" :key="index" :pro="pro" />
          <XposureLoader
            v-if="pending"
            additionalClasses="absolute top-0 left-0 w-full h-full z-50"
            spinnerAdditionalClasses="fixed top-[50vh]"
            showBackground
          />
        </div>
        <div
          v-if="!pros.length"
          class="border border-grayscale-16 bg-grayscale-7 rounded-8 p-8 flex flex-col items-center gap-1"
        >
          <p>No Pros Were Found</p>
          <p class="text-grayscale-80 text-xs text-center">
            Would you like to see someone on Xposure? We're always on the
            lookout for fresh talent to feature.
          </p>
          <OutgoingLink
            additionalClasses="text-accent-orange mt-7"
            href="https://info.xposuremusic.com/suggest-a-professional"
            label="Suggest a Pro"
          />
        </div>
      </div>

      <div v-if="pros.length" class="flex justify-center mb-20">
        <general-button redirect="/industry-pros" height="small">
          Show All Xposure Pros
        </general-button>
      </div>

      <div
        v-if="pros.length"
        class="grid grid-cols-1 lg:grid-cols-2 lg:gap-40 border-y border-grayscale-20"
      >
        <div
          class="pt-16 lg:pb-16 lg:border-none lg:border-b border-grayscale-20 flex flex-col gap-6"
        >
          <p class="text-20">{{ directory.detailTitle }}</p>
          <p>
            {{ directory.detailDescription }}
          </p>
        </div>
        <div class="py-16 flex flex-col gap-6">
          <p class="text-20">Other Xposure Professionals you may like</p>
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div class="flex flex-col gap-y-2">
              <router-link
                class="text-13 text-accent-orange"
                v-for="subpageLink in directory.links1"
                :to="`${subpageLink.link}`"
              >
                {{ subpageLink.title }}
              </router-link>
            </div>
            <div class="flex flex-col gap-y-2">
              <router-link
                class="text-13 text-accent-orange"
                v-for="subpageLink in directory.links2"
                :to="`${subpageLink.link}`"
              >
                {{ subpageLink.title }}
              </router-link>
            </div>
          </div>
        </div>
      </div>

      <div v-if="articles.length" class="flex flex-col gap-10 mt-20">
        <h2 class="uppercase text-2xl font-druk-wide">Latest articles</h2>
        <div
          class="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-6 md:gap-y-8"
        >
          <a
            v-for="(article, index) of articles"
            :key="index"
            class="flex items-center gap-6 hover:cursor-pointer"
            :href="'https://info.xposuremusic.com/article/' + article.slug"
            target="_blank"
          >
            <img
              :src="article.thumbnailImage.url"
              :alt="article.thumbnailImage.alt"
              width="100"
              height="100"
            />
            <div class="flex flex-col gap-3">
              <div class="flex gap-5 items-center text-11">
                <span
                  class="uppercase py-px px-2 bg-grayscale-96 text-grayscale-2 rounded-2 font-semibold"
                >
                  Music Industry
                </span>
                <p>{{ dayjs(article.createdOn).format("MMM D, YYYY") }}</p>
              </div>
              <p class="text-13 font-bold uppercase">
                {{ article.title }}
              </p>
            </div>
          </a>
        </div>
      </div>
    </div>
  </div>
  <Footer />

  <sort-modal v-if="showOrders" @close="showOrders = false"></sort-modal>
</template>

<script setup lang="ts">
import { JobSlug } from "@/types/directory";
import type { DirectoryType } from "@/types/directory";
import XposureLoader from "@/components/loader/XposureLoader.vue";
import { useProStore } from "@/store/pros";
import GeneralButton from "@/components/layout/buttons/GeneralButton.vue";
import SortModal from "@/components/modal/SortModal.vue";
import DirectoryService from "@/services/directory-service";
import dayjs from "dayjs";

import OutgoingLink from "@/components/layout/buttons/OutgoingLink.vue";
import Footer from "@/components/footers/Footer.vue";
import { useWebflowStore } from "@/store/webflow";

const directoryService = new DirectoryService();

const directories = directoryService.getDirectories();

const router = useRouter();

const directory = computed(() => {
  const pathParams = router.currentRoute.value.params.slugs as string[];
  if (!pathParams) {
    return;
  }
  switch (pathParams.length) {
    case 1:
      if (!(pathParams[0] in directories)) {
        showError({
          statusCode: 404,
          statusMessage: "Not Found",
        });
        return;
      }
      return directories[pathParams[0]] as DirectoryType;
    case 2:
      if (!(pathParams[0] in JobSlug)) {
        showError({
          statusCode: 404,
          statusMessage: "Not Found",
        });
        return;
      }

      if (!(pathParams[1] in (directories[pathParams[0]].children || {}))) {
        showError({
          statusCode: 404,
          statusMessage: "Not Found",
        });
        return;
      }

      return directories[pathParams[0]].children![
        pathParams[1]
      ] as DirectoryType;

    default:
      showError({
        statusCode: 404,
        statusMessage: "Not Found",
      });
      break;
  }
});

const proStore = useProStore();
const webflowStore = useWebflowStore();
const showOrders = ref(false);

const showOrdersChanged = () => {
  showOrders.value = !showOrders.value;
};

const query = computed(() => router.currentRoute.value.query);

const apiQuery = computed(() => {
  return {
    ...directory.value?.query,
    ...query.value,
  };
});

const pros = computed(() => proStore.pros);
const articles = computed(() => webflowStore.articles);

await webflowStore.fetchArticles(apiQuery);

const { pending } = await proStore.fetchPros(apiQuery);

useHead({
  title: directory.value?.metaTitle,
  meta: [
    {
      name: "description",
      content: directory.value?.metaDescription,
    },
    {
      name: "og:description",
      content: directory.value?.metaDescription,
    },
    {
      name: "twitter:description",
      content: directory.value?.metaDescription,
    },
    {
      property: "og:title",
      content: directory.value?.metaTitle,
    },
    {
      property: "twitter:title",
      content: directory.value?.metaTitle,
    },
  ],
});
</script>
